import React, { useState } from "react";
import { Spinner } from "../../../atoms/Spinner";
import { DataTable } from "../../../atoms/DataTable";
import { StatusBadge } from "../../../atoms/StatusBadge";
import { EmptyState } from "../../../atoms/EmptyState";
import {
  useTwilioAccount,
  useTwilioNumbers,
  useTwilioSenders,
  useTwilioUsage,
} from "../../../../hooks/providers";

const USAGE_PERIODS: Array<{ value: string; label: string }> = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "thisMonth", label: "This month" },
  { value: "lastMonth", label: "Last month" },
  { value: "yearly", label: "By year" },
  { value: "allTime", label: "All time" },
];
const LOW_BALANCE_THRESHOLD = 20;

// Only SMS and WhatsApp run on Twilio; ignore the rest of the usage catalogue.
const isRelevantUsage = (category: string) =>
  /whatsapp|sms/i.test(category || "");

const PLACEHOLDER_VALUES = ["unknown", "unavailable", "n/a", "-"];
const senderValue = (raw: unknown): string => {
  const value = String(raw ?? "").trim();
  if (!value || PLACEHOLDER_VALUES.includes(value.toLowerCase())) return "—";
  return value;
};

const QUALITY_LABELS: Record<string, string> = {
  high: "High",
  medium: "Medium",
  low: "Low",
};
const senderQuality = (raw: unknown): string => {
  const value = senderValue(raw);
  return QUALITY_LABELS[value.toLowerCase()] ?? value;
};

type SenderOfflineReason = {
  code?: string;
  message?: string;
  more_info?: string;
};

const senderOfflineReasons = (raw: unknown): SenderOfflineReason[] => {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((reason) =>
      typeof reason === "string"
        ? { message: reason }
        : {
            code: (reason as SenderOfflineReason)?.code,
            message: (reason as SenderOfflineReason)?.message,
            more_info: (reason as SenderOfflineReason)?.more_info,
          }
    )
    .filter((reason) => String(reason.message || "").trim());
};

const senderTone = (status: string) =>
  status === "online" ? "success" : status === "offline" ? "failure" : "gray";

const Card: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div className="rounded-lg border border-gray-200 p-4">
    <span className="block text-xs uppercase text-gray-500">{label}</span>
    <div className="mt-1 text-lg font-semibold text-gray-800">{children}</div>
  </div>
);

const TwilioAccountPanel: React.FC<{ customer: string; entity: string }> = ({
  customer,
  entity,
}) => {
  const [period, setPeriod] = useState("thisMonth");
  const account = useTwilioAccount(customer, entity);
  const numbers = useTwilioNumbers(customer, entity);
  const senders = useTwilioSenders(customer, entity);
  const usage = useTwilioUsage(customer, entity, period);

  const lowBalance =
    account.data &&
    typeof account.data.balance === "number" &&
    account.data.balance < LOW_BALANCE_THRESHOLD;
  const senderList: any[] = senders.data?.senders ?? [];
  const usageItems: any[] = (usage.data?.items ?? []).filter((it: any) =>
    isRelevantUsage(it.category)
  );

  return (
    <div className="space-y-6">
      <section className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Card label="Account balance">
          {account.isLoading ? (
            <Spinner size="sm" />
          ) : account.data ? (
            <span className={lowBalance ? "text-red-600" : ""}>
              {account.data.balance} {account.data.currency}
            </span>
          ) : (
            <span className="text-sm text-gray-400">n/a</span>
          )}
        </Card>
        <Card label="Provisioned numbers">
          {numbers.isLoading ? (
            <Spinner size="sm" />
          ) : (
            numbers.data?.count ?? "—"
          )}
        </Card>
        <Card label="WhatsApp senders">
          {senders.isLoading ? <Spinner size="sm" /> : senderList.length}
        </Card>
      </section>

      <section>
        <h3 className="mb-1 text-sm font-semibold text-gray-900">
          WhatsApp sender health
        </h3>
        <p className="mb-2 text-xs text-gray-500">
          Senders without an entity are our own technical numbers (shared
          anchor), not merchants. Quality and messaging limit only appear once a
          sender carries approved Meta traffic.
        </p>
        {senders.isLoading ? (
          <Spinner size="sm" />
        ) : senderList.length === 0 ? (
          <EmptyState title="No sender" bordered />
        ) : (
          <DataTable>
            <DataTable.Head>
              <DataTable.HeadCell>Entity</DataTable.HeadCell>
              <DataTable.HeadCell>Number</DataTable.HeadCell>
              <DataTable.HeadCell>Status</DataTable.HeadCell>
              <DataTable.HeadCell>Quality</DataTable.HeadCell>
              <DataTable.HeadCell>Limit</DataTable.HeadCell>
              <DataTable.HeadCell>Offline reasons</DataTable.HeadCell>
            </DataTable.Head>
            <DataTable.Body>
              {senderList.map((s) => (
                <DataTable.Row key={s.sender_sid}>
                  <DataTable.Cell>
                    {s.entity_identification || (
                      <span className="text-gray-400">technical</span>
                    )}
                  </DataTable.Cell>
                  <DataTable.Cell>{s.from}</DataTable.Cell>
                  <DataTable.Cell>
                    <StatusBadge color={senderTone(s.status)} size="xs">
                      {s.status}
                    </StatusBadge>
                  </DataTable.Cell>
                  <DataTable.Cell>{senderQuality(s.quality)}</DataTable.Cell>
                  <DataTable.Cell>
                    {senderValue(s.waba_messaging_limit)}
                  </DataTable.Cell>
                  <DataTable.Cell className="text-xs text-gray-500">
                    {senderOfflineReasons(s.offline_reasons).length === 0
                      ? "—"
                      : senderOfflineReasons(s.offline_reasons).map(
                          (reason) => (
                            <span
                              key={`${reason.code}-${reason.message}`}
                              className="block"
                            >
                              {reason.more_info ? (
                                <a
                                  href={reason.more_info}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-blue-600 underline"
                                >
                                  {reason.message}
                                </a>
                              ) : (
                                reason.message
                              )}
                            </span>
                          )
                        )}
                  </DataTable.Cell>
                </DataTable.Row>
              ))}
            </DataTable.Body>
          </DataTable>
        )}
      </section>

      <section>
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900">
            SMS &amp; WhatsApp spend
          </h3>
          <select
            className="rounded-md border border-gray-300 p-1.5 text-sm"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
          >
            {USAGE_PERIODS.map((usagePeriod) => (
              <option key={usagePeriod.value} value={usagePeriod.value}>
                {usagePeriod.label}
              </option>
            ))}
          </select>
        </div>
        {usage.isLoading ? (
          <Spinner size="sm" />
        ) : usageItems.length === 0 ? (
          <EmptyState
            title="No SMS or WhatsApp usage for this period"
            bordered
          />
        ) : (
          <DataTable>
            <DataTable.Head>
              <DataTable.HeadCell>Category</DataTable.HeadCell>
              <DataTable.HeadCell numeric>Count</DataTable.HeadCell>
              <DataTable.HeadCell numeric>Price</DataTable.HeadCell>
            </DataTable.Head>
            <DataTable.Body>
              {usageItems.map((it, index) => (
                <DataTable.Row key={`${it.category}-${index}`}>
                  <DataTable.Cell>{it.category}</DataTable.Cell>
                  <DataTable.Cell numeric>{it.count}</DataTable.Cell>
                  <DataTable.Cell numeric>
                    {it.price} {it.currency}
                  </DataTable.Cell>
                </DataTable.Row>
              ))}
            </DataTable.Body>
          </DataTable>
        )}
      </section>
    </div>
  );
};

export default TwilioAccountPanel;
